<script setup lang="ts">
import { computed, ref } from "vue";

import type { TableDropdownProps } from "@/components/tableDropdowns/types";

import Dropdown from "@/dsComponents/Dropdown/Dropdown.vue";
import DropdownItem from "@/dsComponents/Dropdown/DropdownItem.vue";
import { DropdownDirections } from "@/dsComponents/Dropdown/types";

const props = withDefaults(defineProps<TableDropdownProps>(), {
    mode: "click",
    nostyle: false,
    width: "auto",
    height: "auto",
    direction: DropdownDirections.AUTOMATIC,
    open: true,
    dropdownStyle() {
        return {
            triggerTextColor: "$text-sub-600",
        };
    },
});

const triggerText = ref<HTMLElement>();
const triggerZone = ref<HTMLElement>();
const isEllipsisTextActive = ref(false);
const triggerComponent = ref();
const checkEllipsis = () => {
    // Use >= because if the text or components has the same width as the trigger zone, it surely has ellipsis
    if (triggerText.value && triggerZone.value) {
        isEllipsisTextActive.value = triggerText.value.offsetWidth >= triggerZone.value.offsetWidth;
    }

    // biome-ignore lint/complexity/useOptionalChain: Init Biome
    const componentEl = triggerComponent.value && triggerComponent.value.$el;
    if (componentEl && triggerZone.value) {
        isEllipsisTextActive.value = componentEl.offsetWidth >= triggerZone.value.offsetWidth;
    }
};

const canDisplayDropdown = computed(() => {
    return (
        (props.open && props.items && props.items.length > 1) ||
        isEllipsisTextActive.value ||
        (props.forceOpen && props.items && props.items.length > 0)
    );
});
</script>
<template>
    <Dropdown
        :width="width"
        :height="height"
        :nostyle="nostyle"
        :mode="mode"
        :direction="direction"
        :disabled="!canDisplayDropdown"
        close-on-descendant
    >
        <template #trigger-zone>
            <div class="triggerZone" ref="triggerZone" @mouseenter="checkEllipsis">
                <span
                    v-if="typeof trigger === 'string'"
                    ref="triggerText"
                    :class="['table-dropdown__trigger-text typo-xs-regular', dropdownStyle.triggerTextClass]"
                    :style="{ color: dropdownStyle.triggerTextColor }"
                >
                    {{ trigger }}
                </span>
                <component v-else ref="triggerComponent" :is="trigger" v-bind="componentProps" />
            </div>
        </template>
        <template #dropdown-content>
            <div class="dropdown-content">
                <DropdownItem v-if="dropdownTitle" :label="dropdownTitle" font-size="12" cursor="default" font-weight="400" no-hover />
                <!-- biome-ignore lint/correctness/noVueVIfWithVFor: Init Biome -->
                <!-- biome-ignore lint/correctness/useVueVForKey: Init Biome -->
                <div v-if="items" v-for="item in items" class="dropdown-item">
                    <i v-if="item.icon" :class="`ti ti-${item.icon}`" :style="{ color: item.iconColor }" />
                    <component v-if="item.component" :is="item.component" v-bind="item.componentProps" />
                    <p
                        :class="['table-dropdown__dropdown-text typo-xs-regular', dropdownStyle.contentTextClass]"
                        :style="item.textStyle"
                        v-html="item.text"
                    />
                </div>
            </div>
        </template>
    </Dropdown>
</template>
<style scoped lang="scss">
.triggerZone {
    max-width: 100%;
    max-height: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.table-dropdown__dropdown-text {
    white-space: nowrap;
}

.dropdown-content {
    display: flex;
    flex-direction: column;
    gap: var(--spacing-md, 8px);

    .dropdown-item {
        display: flex;
        align-items: center;
        gap: var(--spacing-md, 8px);
        padding: 0 var(--spacing-xs, 4px);
    }
}
</style>
